import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ManagerSidebar from '../components/ManagerSidebar';
import ManagerTopHeader from '../components/ManagerTopHeader';

import { API_BASE } from '../../config/runtime';

const getStoredUser = () => {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

/* ─── Minimal keyframes Tailwind can't express natively ─────────────────── */
if (typeof document !== 'undefined' && !document.getElementById('msc-keyframes')) {
  const style = document.createElement('style');
  style.id = 'msc-keyframes';
  style.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&family=DM+Sans:wght@400;500;600&family=DM+Mono:wght@400;500&display=swap');

    .msc-fade-up { opacity:0; transform:translateY(14px); animation: mscFadeUp .38s ease forwards; }
    @keyframes mscFadeUp { to { opacity:1; transform:translateY(0); } }

    .msc-spin { animation: mscSpin .8s linear infinite; }
    @keyframes mscSpin { to { transform: rotate(360deg); } }

    .msc-input:focus { border-color: #2563EB; box-shadow: 0 0 0 3px rgba(37,99,235,.1); background: #fff; }
    .msc-input::placeholder { color: #94A3B8; }

    .msc-select:focus { border-color: #2563EB; box-shadow: 0 0 0 3px rgba(37,99,235,.1); }

    .msc-skeleton {
      background: linear-gradient(90deg,#F1F5F9 25%,#E8EDF2 50%,#F1F5F9 75%);
      background-size: 200% 100%;
      animation: mscShimmer 1.4s infinite; border-radius: 6px;
    }
    @keyframes mscShimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

    /* Responsive table: card layout on small screens */
    @media (max-width: 680px) {
      .msc-thead { display: none !important; }
      .msc-trow {
        display: flex !important; flex-direction: column; gap: 0;
        border: 1px solid #E2E8F0; border-radius: 14px;
        margin-bottom: 10px; overflow: hidden;
        box-shadow: 0 2px 8px rgba(15,23,42,.05);
      }
      .msc-trow:hover { background: #fff !important; }
      .msc-tcell {
        display: flex !important; justify-content: space-between; align-items: center; gap: 8px;
        padding: 10px 14px; border-bottom: 1px solid #F1F5F9;
      }
      .msc-tcell:last-child { border-bottom: none; }
      .msc-tcell::before {
        content: attr(data-label);
        font-family: 'Sora', sans-serif; font-size: 10px; font-weight: 700;
        letter-spacing: .08em; text-transform: uppercase; color: #94A3B8;
        white-space: nowrap; flex-shrink: 0;
      }
    }

    .msc-clamp-pad { padding: clamp(16px, 4vw, 40px); }
    .msc-clamp-hero-pad { padding: clamp(24px, 4vw, 36px); }
    .msc-clamp-title { font-size: clamp(18px, 3vw, 24px); }
    .msc-clamp-card-pad { padding: clamp(20px, 4vw, 32px); }
  `;
  document.head.appendChild(style);
}

/* ─── Icons ───────────────────────────────────────────────────────────────── */
const IconCheck = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const IconWarn = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);
const IconSend = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
  </svg>
);
const IconRefresh = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
  </svg>
);
const IconTicket = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <path d="M15 5v2M15 11v2M15 17v2M5 5h14a2 2 0 0 1 2 2v3a2 2 0 0 0 0 4v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-3a2 2 0 0 0 0-4V7a2 2 0 0 1 2-2z"/>
  </svg>
);
const IconClip = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
  </svg>
);

/* ─── Priority config ─────────────────────────────────────────────────────── */
const PRIORITY_CFG = {
  Low:    { bg: '#F0FDF4', color: '#16A34A', dot: '#22C55E' },
  Medium: { bg: '#EFF6FF', color: '#2563EB', dot: '#3B82F6' },
  High:   { bg: '#FFF7ED', color: '#C2410C', dot: '#F97316' },
  Urgent: { bg: '#FEF2F2', color: '#DC2626', dot: '#EF4444' },
};

const PriorityBadge = ({ priority }) => {
  const cfg = PRIORITY_CFG[priority] || PRIORITY_CFG.Medium;
  return (
    <span
      className="inline-flex items-center gap-[5px] text-[10px] font-bold tracking-[0.05em] px-[9px] py-[3px] rounded-full"
      style={{ background: cfg.bg, color: cfg.color, fontFamily: 'Sora, sans-serif' }}
    >
      <svg width="6" height="6" viewBox="0 0 8 8"><circle cx="4" cy="4" r="4" fill={cfg.dot} /></svg>
      {priority || 'Medium'}
    </span>
  );
};

const StatusBadge = ({ status }) => {
  const resolved = (status || '').toLowerCase() === 'resolved';
  return (
    <span
      className="inline-flex items-center gap-[5px] text-[10px] font-bold tracking-[0.04em] px-[10px] py-1 rounded-full"
      style={{
        fontFamily: 'Sora, sans-serif',
        background: resolved ? '#DCFCE7' : '#FEF9C3',
        color: resolved ? '#15803D' : '#A16207',
      }}
    >
      <svg width="6" height="6" viewBox="0 0 8 8">
        <circle cx="4" cy="4" r="4" fill={resolved ? '#22C55E' : '#EAB308'} />
      </svg>
      {resolved ? 'Resolved' : 'Pending'}
    </span>
  );
};

/* ─── Skeleton rows ───────────────────────────────────────────────────────── */
const SkeletonRows = () => (
  <>
    {[...Array(3)].map((_, i) => (
      <div key={i} className="msc-trow grid border-b border-slate-50 last:border-b-0" style={{ gridTemplateColumns: '80px 90px 1fr 90px 90px 110px' }}>
        {[60, 70, 160, 70, 70, 90].map((w, j) => (
          <div key={j} className="msc-tcell px-3 py-[14px]">
            <div className="msc-skeleton h-[11px]" style={{ width: w }} />
          </div>
        ))}
      </div>
    ))}
  </>
);

/* ─── Main Component ──────────────────────────────────────────────────────── */
const ManagerSettings = () => {
  const user = useMemo(() => getStoredUser(), []);
  const [form, setForm] = useState({ subject: '', priority: 'Medium', message: '' });
  const [submissionMode, setSubmissionMode] = useState('Ticket');
  const [attachment, setAttachment] = useState(null);
  const [attachmentName, setAttachmentName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [submissions, setSubmissions] = useState([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);

  const fetchSubmissions = useCallback(async () => {
    if (!user?.user_id) return;
    setLoadingSubmissions(true);
    try {
      const res = await fetch(`${API_BASE}/get_manager_tickets.php?user_id=${encodeURIComponent(String(user.user_id))}`);
      const data = await res.json();
      if (data.success) setSubmissions(data.tickets || []);
    } catch {}
    finally { setLoadingSubmissions(false); }
  }, [user?.user_id]);

  useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: '', message: '' });
    if (!form.subject.trim() || !form.message.trim()) {
      setStatus({ type: 'error', message: 'Subject and details are required.' });
      return;
    }
    if (!user?.user_id) {
      setStatus({ type: 'error', message: 'No logged-in manager found. Please sign in again.' });
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = new FormData();
      payload.append('user_id', String(Number(user.user_id)));
      payload.append('type', submissionMode);
      payload.append('subject', form.subject.trim());
      payload.append('priority', form.priority);
      payload.append('message', form.message.trim());
      if (attachment) payload.append('attachment', attachment);

      const res = await fetch(`${API_BASE}/create_ticket.php`, { method: 'POST', body: payload });
      const data = await res.json();

      if (!data.success) {
        setStatus({ type: 'error', message: data.message || 'Failed to submit.' });
        setIsSubmitting(false);
        return;
      }
      setStatus({ type: 'success', message: `Submitted successfully. Reference #${data.ticket?.ticket_id ?? 'N/A'}` });
      setForm(p => ({ ...p, subject: '', message: '' }));
      setAttachment(null);
      setAttachmentName('');
      fetchSubmissions();
    } catch (err) {
      setStatus({ type: 'error', message: `Failed to submit: ${err.message}` });
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ─── Render ──────────────────────────────────────────────────────────── */
  return (
    <div className="flex min-h-screen bg-slate-50" style={{ fontFamily: 'DM Sans, sans-serif' }}>
      <ManagerSidebar />

      <div className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden">
        <ManagerTopHeader pageTitle="Support Center" />

        <div className="flex-1 overflow-y-auto msc-clamp-pad">
          <div className="max-w-[860px] mx-auto flex flex-col gap-6">

            {/* ── Hero banner ── */}
            <div
              className="msc-fade-up flex items-center gap-5 flex-wrap rounded-[20px] msc-clamp-hero-pad"
              style={{ background: 'linear-gradient(135deg, #1E3A8A 0%, #2563EB 60%, #3B82F6 100%)' }}
            >
              <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center flex-shrink-0 text-white">
                <IconTicket />
              </div>
              <div>
                <h1 className="msc-clamp-title font-extrabold text-white m-0" style={{ fontFamily: 'Sora, sans-serif' }}>
                  Support Center
                </h1>
                <p className="text-white/75 text-[13px] mt-1 mb-0">
                  Submit operational concerns and branch requests to the admin team.
                </p>
              </div>
            </div>

            {/* ── Status alert ── */}
            {status.message && (
              <div
                className={`msc-fade-up flex items-start gap-2.5 rounded-xl px-4 py-[13px] text-[13px] font-medium border ${
                  status.type === 'success'
                    ? 'bg-green-50 border-green-200 text-green-700'
                    : 'bg-red-50 border-red-200 text-red-600'
                }`}
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                {status.type === 'success' ? <IconCheck /> : <IconWarn />}
                <span>{status.message}</span>
              </div>
            )}

            {/* ── Submission form ── */}
            <div
              className="msc-fade-up bg-white border border-slate-200 rounded-[20px] msc-clamp-card-pad shadow-[0_4px_24px_rgba(15,23,42,0.06)]"
              style={{ animationDelay: '60ms' }}
            >
              <h2 className="font-extrabold text-base text-slate-900 m-0 mb-6" style={{ fontFamily: 'Sora, sans-serif' }}>
                New Submission
              </h2>

              <form onSubmit={handleSubmit} className="flex flex-col gap-5">

                {/* Type + Priority row */}
                <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>

                  {/* Mode toggle */}
                  <div>
                    <label className="block text-xs font-bold text-slate-700 tracking-[0.04em] mb-[7px]" style={{ fontFamily: 'Sora, sans-serif' }}>
                      Submission Type
                    </label>
                    <div className="inline-flex bg-slate-100 rounded-xl p-1 gap-1">
                      {['Ticket', 'Request'].map(mode => (
                        <button
                          key={mode}
                          type="button"
                          className={`text-xs font-bold px-5 py-2 rounded-[9px] border-none cursor-pointer transition-all duration-150
                            ${submissionMode === mode
                              ? 'bg-blue-600 text-white shadow-[0_2px_10px_rgba(37,99,235,0.3)]'
                              : 'bg-transparent text-slate-500 hover:bg-slate-200 hover:text-slate-700'
                            }`}
                          style={{ fontFamily: 'Sora, sans-serif' }}
                          onClick={() => setSubmissionMode(mode)}
                        >
                          {mode}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Priority */}
                  <div>
                    <label className="block text-xs font-bold text-slate-700 tracking-[0.04em] mb-[7px]" style={{ fontFamily: 'Sora, sans-serif' }}>
                      Priority
                    </label>
                    <select
                      className="msc-select w-full py-[11px] pl-[14px] pr-9 border-[1.5px] border-slate-200 rounded-[10px] text-[13px] font-semibold bg-[#FAFBFC] text-slate-700 outline-none appearance-none cursor-pointer transition-all box-border"
                      style={{
                        fontFamily: 'Sora, sans-serif',
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%2394A3B8' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 12px center',
                      }}
                      name="priority"
                      value={form.priority}
                      onChange={e => setForm(p => ({ ...p, priority: e.target.value }))}
                    >
                      {['Low', 'Medium', 'High', 'Urgent'].map(p => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Subject */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 tracking-[0.04em] mb-[7px]" style={{ fontFamily: 'Sora, sans-serif' }}>
                    Subject <span className="text-red-500">*</span>
                  </label>
                  <input
                    className="msc-input w-full px-[14px] py-[11px] border-[1.5px] border-slate-200 rounded-[10px] text-sm font-medium bg-[#FAFBFC] text-slate-900 outline-none transition-all box-border"
                    style={{ fontFamily: 'DM Sans, sans-serif' }}
                    type="text"
                    name="subject"
                    value={form.subject}
                    onChange={e => setForm(p => ({ ...p, subject: e.target.value }))}
                    maxLength={150}
                    placeholder="e.g. Need additional therapist schedule slots"
                    required
                  />
                </div>

                {/* Details */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 tracking-[0.04em] mb-[7px]" style={{ fontFamily: 'Sora, sans-serif' }}>
                    Details <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    className="msc-input w-full px-[14px] py-[11px] border-[1.5px] border-slate-200 rounded-[10px] text-sm font-medium bg-[#FAFBFC] text-slate-900 outline-none transition-all box-border resize-y"
                    style={{ fontFamily: 'DM Sans, sans-serif' }}
                    name="message"
                    value={form.message}
                    onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
                    rows={5}
                    maxLength={2000}
                    placeholder="Describe your issue or request clearly…"
                    required
                  />
                  <div className="text-right mt-1 text-[11px] text-slate-400" style={{ fontFamily: 'Sora, sans-serif' }}>
                    {form.message.length}/2000
                  </div>
                </div>

                {/* Attachment */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 tracking-[0.04em] mb-[7px]" style={{ fontFamily: 'Sora, sans-serif' }}>
                    Attachment <span className="font-medium text-slate-400">(optional)</span>
                  </label>
                  <label
                    className="flex items-center gap-2.5 border-[1.5px] border-dashed border-slate-300 rounded-xl px-4 py-3 bg-slate-50 cursor-pointer transition-colors hover:border-blue-300"
                  >
                    <span className="text-slate-500 flex"><IconClip /></span>
                    <span
                      className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-[13px]"
                      style={{ fontFamily: 'DM Sans, sans-serif', color: attachmentName ? '#0F172A' : '#94A3B8' }}
                    >
                      {attachmentName || 'Click to attach an image…'}
                    </span>
                    {attachmentName && (
                      <button
                        type="button"
                        className="text-[10px] font-bold text-red-500 bg-red-50 border-none rounded-md px-2 py-[3px] cursor-pointer"
                        style={{ fontFamily: 'Sora, sans-serif' }}
                        onClick={e => { e.preventDefault(); setAttachment(null); setAttachmentName(''); }}
                      >
                        Remove
                      </button>
                    )}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp"
                      className="hidden"
                      onChange={e => {
                        const f = e.target.files?.[0] || null;
                        setAttachment(f);
                        setAttachmentName(f?.name || '');
                      }}
                    />
                  </label>
                  <p className="text-[11px] text-slate-400 mt-1.5" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                    JPG, PNG, WEBP — max 5 MB
                  </p>
                </div>

                {/* Submit */}
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex items-center justify-center gap-[7px] font-bold text-xs tracking-[0.06em] px-6 py-[11px] rounded-[11px] border-none cursor-pointer transition-all duration-200 active:scale-95 bg-blue-600 text-white hover:bg-blue-700 hover:shadow-[0_4px_14px_rgba(37,99,235,0.3)] disabled:opacity-55 disabled:cursor-not-allowed disabled:shadow-none disabled:scale-100"
                    style={{ fontFamily: 'Sora, sans-serif' }}
                  >
                    {isSubmitting
                      ? <>
                          <div className="msc-spin w-[14px] h-[14px] border-2 border-white/40 border-t-white rounded-full" />
                          Submitting…
                        </>
                      : <><IconSend />Submit {submissionMode}</>
                    }
                  </button>
                </div>
              </form>
            </div>

            {/* ── Submissions table ── */}
            <div
              className="msc-fade-up bg-white border border-slate-200 rounded-[20px] overflow-hidden shadow-[0_4px_24px_rgba(15,23,42,0.06)]"
              style={{ animationDelay: '120ms' }}
            >
              {/* Table header bar */}
              <div className="px-6 py-5 border-b border-slate-50 flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <h2 className="font-extrabold text-base text-slate-900 m-0" style={{ fontFamily: 'Sora, sans-serif' }}>
                    My Submissions
                  </h2>
                  <p className="text-xs text-slate-500 mt-0.5 mb-0">
                    {submissions.length} total ticket{submissions.length !== 1 ? 's' : ''} & request{submissions.length !== 1 ? 's' : ''}
                  </p>
                </div>
                <button
                  onClick={fetchSubmissions}
                  className="flex items-center gap-1.5 text-[11px] font-bold text-slate-500 bg-slate-100 border-none rounded-[9px] px-[14px] py-2 cursor-pointer transition-colors hover:bg-slate-200"
                  style={{ fontFamily: 'Sora, sans-serif' }}
                >
                  <span className={loadingSubmissions ? 'msc-spin' : ''}><IconRefresh /></span>
                  Refresh
                </button>
              </div>

              {/* Column headers */}
              <div
                className="msc-thead msc-trow grid bg-slate-50 border-b border-slate-200"
                style={{ gridTemplateColumns: '80px 90px 1fr 90px 90px 110px' }}
              >
                {['Ref #', 'Type', 'Subject', 'Priority', 'Status', 'Date'].map(h => (
                  <div
                    key={h}
                    className="msc-tcell px-3 pt-3 pb-3 text-[10px] font-extrabold tracking-[0.1em] uppercase text-slate-400"
                    style={{ fontFamily: 'Sora, sans-serif' }}
                  >
                    {h}
                  </div>
                ))}
              </div>

              {/* Rows */}
              <div>
                {loadingSubmissions && <SkeletonRows />}

                {!loadingSubmissions && submissions.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-[60px] px-6 text-center">
                    <div className="w-12 h-12 rounded-[14px] bg-blue-50 flex items-center justify-center mx-auto mb-[14px] text-blue-600">
                      <IconTicket />
                    </div>
                    <p className="font-bold text-slate-400 m-0 mb-1.5" style={{ fontFamily: 'Sora, sans-serif' }}>
                      No submissions yet
                    </p>
                    <p className="text-slate-300 text-[13px] m-0">
                      Use the form above to submit your first ticket or request.
                    </p>
                  </div>
                )}

                {!loadingSubmissions && submissions.map((item, idx) => (
                  <div
                    key={item.ticket_id}
                    className="msc-trow msc-fade-up grid border-b border-slate-50 last:border-b-0 transition-colors hover:bg-[#FAFBFF]"
                    style={{ gridTemplateColumns: '80px 90px 1fr 90px 90px 110px', animationDelay: `${idx * 35}ms` }}
                  >
                    {/* Ref # */}
                    <div className="msc-tcell px-3 py-[14px] text-[13px] text-slate-500" data-label="Ref #" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                      <span
                        className="text-xs font-semibold text-blue-600 bg-blue-50 px-[7px] py-[2px] rounded-md"
                        style={{ fontFamily: 'DM Mono, monospace' }}
                      >
                        #{item.ticket_id}
                      </span>
                    </div>

                    {/* Type */}
                    <div className="msc-tcell px-3 py-[14px] text-[13px] text-slate-500" data-label="Type" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                      <span className="text-[11px] font-bold text-slate-500 bg-slate-100 px-[9px] py-[3px] rounded-full" style={{ fontFamily: 'Sora, sans-serif' }}>
                        {item.ticket_type || 'Ticket'}
                      </span>
                    </div>

                    {/* Subject */}
                    <div className="msc-tcell px-3 py-[14px] text-[13px] text-slate-500" data-label="Subject" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                      <span
                        className="text-[13px] font-semibold text-slate-900 overflow-hidden"
                        style={{ display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical' }}
                      >
                        {item.subject || '—'}
                      </span>
                    </div>

                    {/* Priority */}
                    <div className="msc-tcell px-3 py-[14px] text-[13px] text-slate-500" data-label="Priority" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                      <PriorityBadge priority={item.priority || 'Medium'} />
                    </div>

                    {/* Status */}
                    <div className="msc-tcell px-3 py-[14px] text-[13px] text-slate-500" data-label="Status" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                      <StatusBadge status={item.status} />
                    </div>

                    {/* Date */}
                    <div className="msc-tcell px-3 py-[14px] text-xs text-slate-500" data-label="Date" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                      {item.created_at ? String(item.created_at).slice(0, 10) : '—'}
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default ManagerSettings;
